import React from "react";
import { PageHero, CtaBanner } from "../pages/PageShell";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import Deployment from "../Deployment/Deployment";
import { PlatformDiagram } from "../ui/Diagrams";
import { IntelFeatures } from "../Intelligence/Intelligence";
import { INTEGRATIONS } from "../../data/site";
import { useSeo } from "../../lib/seo";
import { Activity, Cloud, Cpu, Layers, Shield, Gauge, Bell, Grid, Check } from "../ui/Icons";

const CORE = [
  { icon: Grid, title: "Unified video intelligence", text: "Centralized AI-powered monitoring that integrates all video inputs into a single intelligent interface, across sites and camera vendors." },
  { icon: Activity, title: "Real-time analytics", text: "Detections, tracks and zone events are computed as frames arrive, so dashboards and alerts reflect what is happening now." },
  { icon: Cloud, title: "Cloud processing", text: "Elastic GPU inference with centralized model management and long-term storage in the cloud of your choice." },
  { icon: Cpu, title: "Edge computing", text: "Optimized models on NVIDIA Jetson and other edge devices for millisecond latency and offline resilience." },
  { icon: Layers, title: "Hybrid models", text: "Edge inference with cloud aggregation: only events and clips leave the site, updates flow back down." },
  { icon: Shield, title: "Privacy by design", text: "Role-based access, on-premise options and data minimization for regulated environments." },
];

const ANALYTICS = [
  { icon: Shield, title: "Safety compliance", items: ["PPE detection", "Restricted-zone intrusion", "Proximity to machinery"] },
  { icon: Gauge, title: "Operational efficiency", items: ["Counting and throughput", "Dwell time and queues", "Asset and vehicle tracking"] },
  { icon: Bell, title: "Anomaly detection", items: ["Unusual motion patterns", "Loitering and abandoned objects", "Process deviations"] },
];

export default function VisionPlatform() {
  useSeo({
    title: "Vision Platform: one interface for every camera | Episcope AI",
    description:
      "Enterprise-scale AI monitoring with real-time detection, tracking and analytics across every camera and site. RTSP, ONVIF, WebRTC and VMS integrations; edge, cloud or hybrid deployment; LLM search and AI agents on top.",
    path: "/vision-platform",
  });

  return (
    <div className="page">
      <div className="container">
        <PageHero
          eyebrow="Vision Platform"
          title="One intelligent interface for every camera you run."
          lead="Enterprise-scale AI surveillance with real-time monitoring and predictive analytics. Ingest any stream, run the right model on the right hardware and act on what the cameras see."
          chips={["Real-time", "Multi-site", "Any camera", "Edge · Cloud · Hybrid", "LLM & agents"]}
        />

        <div className="stack">
          <Reveal>
            <PlatformDiagram />
          </Reveal>

          <section>
            <SectionHead
              eyebrow="Core platform"
              title="Everything your video needs, in one place."
            />
            <div className="fgrid3">
              {CORE.map((c, i) => (
                <Reveal key={c.title} className="feature card card--hover" delay={i * 0.06}>
                  <span className="icon-box">
                    <c.icon />
                  </span>
                  <h3 className="feature__title">{c.title}</h3>
                  <p className="feature__text">{c.text}</p>
                </Reveal>
              ))}
            </div>
          </section>

          <section>
            <SectionHead
              eyebrow="Cross-platform integration"
              title="Seamless connectivity with the cameras and systems you already own."
              lead="Standard protocols first, VMS and data-platform integrations second, and the security controls an enterprise review expects. No forklift upgrade, no proprietary lock-in."
            />
            <div className="integ">
              {INTEGRATIONS.map((g, i) => (
                <Reveal key={g.title} className="integ__card card card--hover" delay={i * 0.06}>
                  <span className="integ__idx mono">0{i + 1}</span>
                  <h3 className="feature__title">{g.title}</h3>
                  <p className="feature__text">{g.lead}</p>
                  <ul className="check-list integ__list">
                    {g.items.map((it) => (
                      <li key={it}>
                        <Check />
                        {it}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>
            <p className="integ__note mono">
              Something missing? Most integrations start from the VMS API or a standard protocol;
              we scope the rest during discovery.
            </p>
          </section>

          <section>
            <SectionHead
              eyebrow="Intelligence layer"
              title="Ask your cameras questions. Let agents handle the follow-up."
              lead="On top of detection and tracking, a vision-language layer answers plain-language questions about what happened, a language model writes the reports, and AI agents act within the guardrails you set."
            />
            <IntelFeatures compact />
          </section>

          <section>
            <SectionHead
              eyebrow="Custom analytics"
              title="Transform raw video into actionable insights."
            />
            <div className="fgrid3">
              {ANALYTICS.map((a, i) => (
                <Reveal key={a.title} className="feature card card--hover" delay={i * 0.06}>
                  <span className="icon-box">
                    <a.icon />
                  </span>
                  <h3 className="feature__title">{a.title}</h3>
                  <ul className="check-list">
                    {a.items.map((it) => (
                      <li key={it}>
                        <Check />
                        {it}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>
          </section>
        </div>
      </div>

      <div style={{ marginTop: "clamp(48px, 8vw, 96px)" }}>
        <div className="container">
          <SectionHead
            eyebrow="Deployment options"
            title="Choose where inference runs."
            lead="From a single Jetson Orin Nano behind one camera to a GPU cluster serving thousands of streams, the same platform, the same interface."
          />
        </div>
        <Deployment compact id="platform-deploy" />
      </div>

      <div className="container">
        <CtaBanner />
      </div>
    </div>
  );
}
